import React, { useState } from 'react';
import config from '../../config';
import CadreEtape from './CadreEtape';
import { CONNECTEURS, urlAutorisation } from '../../constants/connecteurs';
import FormulaireMatomo from '../connectors/FormulaireMatomo';
import FormulairePiano from '../connectors/FormulairePiano';
import FormulaireChatGptAds from '../connectors/FormulaireChatGptAds';
import FormulaireAwin from '../connectors/FormulaireAwin';
import FormulairePrestashop from '../connectors/FormulairePrestashop';

const API_URL = config.API_URL;

/**
 * Quatrième étape : brancher une source de données.
 *
 * ## Deux manières d'entrer, un seul écran
 *
 * Sept connecteurs passent par une autorisation chez le fournisseur : on quitte la page, on
 * revient. Quatre se branchent sur place par saisie d'une clé. Les présenter dans deux listes
 * séparées aurait fait porter à l'utilisateur une distinction qui ne le regarde pas — c'est
 * pourquoi les formulaires ont été sortis de l'écran Connecteurs
 * (`components/connectors/`) : pour qu'ils puissent s'ouvrir ici aussi, sans être recopiés.
 *
 * ## Le retour n'a rien à transporter
 *
 * `returnTo=onboarding` ramène sur `#onboarding`, mais ce n'est qu'un confort d'affichage : au
 * retour, c'est le serveur qui dit à quelle étape on en est, en constatant qu'une connexion
 * existe. Un consentement refusé, un onglet fermé pendant la redirection ou un retour sans hash
 * aboutissent donc au même endroit — cette étape, ou la suivante si la connexion a abouti.
 *
 * ⚠️ Matomo par OAuth fait exception : son callback ne lit pas `returnTo` et ramène à la racine
 * du site. C'est sans conséquence ici, pour la raison qui précède.
 *
 * ## Ce que les quatre formulaires font de plus
 *
 * Matomo, Piano, ChatGPT Ads et Awin **créent leurs comptes au moment de la connexion** : une
 * clé vaut un compte, un jeton en ouvre plusieurs, une instance importe ses sites. L'étape
 * suivante s'en trouve déjà faite pour eux, ce que la lecture d'état constate d'elle-même.
 */
function EtapeConnecteur({ token, onAvance }) {
  const [formulaireOuvert, setFormulaireOuvert] = useState(null);
  const [erreur, setErreur] = useState(null);

  const choisir = (connecteur) => {
    setErreur(null);
    if (connecteur.mode === 'formulaire') {
      setFormulaireOuvert(connecteur.cle);
      return;
    }
    window.location.href = urlAutorisation(API_URL, connecteur, token, 'onboarding');
  };

  const apresFormulaire = async () => {
    setFormulaireOuvert(null);
    await onAvance();
  };

  return (
    <>
      <CadreEtape
        titre="Connectez vos données"
        explication="Choisissez une première source. Vous pourrez en ajouter d’autres à tout moment."
        erreur={erreur}
        largeur="640px"
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '12px'
        }}>
          {CONNECTEURS.map((c) => (
            <button
              key={c.cle}
              onClick={() => choisir(c)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: '10px', padding: '18px 12px', background: 'white',
                border: '1px solid #e5e7eb', borderRadius: '10px', cursor: 'pointer'
              }}
            >
              <img src={c.logo} alt="" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
              <span style={{ fontSize: '13px', color: '#374151', textAlign: 'center' }}>{c.libelle}</span>
            </button>
          ))}
        </div>
      </CadreEtape>

      <FormulaireMatomo
        ouvert={formulaireOuvert === 'MATOMO'}
        onFermer={() => setFormulaireOuvert(null)}
        token={token}
        onConnecte={apresFormulaire}
      />
      <FormulairePiano
        ouvert={formulaireOuvert === 'PIANO_ANALYTICS'}
        onFermer={() => setFormulaireOuvert(null)}
        token={token}
        onConnecte={apresFormulaire}
      />
      <FormulaireChatGptAds
        ouvert={formulaireOuvert === 'OPENAI_ADS'}
        onFermer={() => setFormulaireOuvert(null)}
        token={token}
        onConnecte={apresFormulaire}
      />
      <FormulaireAwin
        ouvert={formulaireOuvert === 'AWIN'}
        onFermer={() => setFormulaireOuvert(null)}
        token={token}
        onConnecte={apresFormulaire}
      />
      <FormulairePrestashop
        ouvert={formulaireOuvert === 'PRESTASHOP'}
        onFermer={() => setFormulaireOuvert(null)}
        token={token}
        onConnecte={apresFormulaire}
      />
    </>
  );
}

export default EtapeConnecteur;
